// Branded sign-up (Sleek's own screen). Posts to /api/auth/register → creates the Keycloak user
// (tenant realm) + identity.user + their resettable demo company. Login stays direct-grant.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { theme, toggleTheme } from '../shared/theme.js';
import { icon } from '../shared/icons.js';

export function registerScreen(){
  const name     = signal('');
  const email    = signal('');
  const password = signal('');
  const kind     = signal('business');       // business | accountant
  const error    = signal('');
  const loading  = signal(false);
  const done     = signal(null);             // set to the created account on success

  // Focus the first field on mount. Native `autofocus` only fires on a fresh document load,
  // not when the router swaps this view in (SPA nav from /login), so focus it after mount.
  setTimeout(() => document.querySelector('.login-wrap input[autocomplete="name"]')?.focus(), 0);

  async function submit(e){
    e.preventDefault();
    loading.value = true; error.value = '';
    try {
      await api.post('/auth/register', { name: name.value, email: email.value,
                                         password: password.value, signup_kind: kind.value });
      done.value = { name: name.value, email: email.value, kind: kind.value };
    } catch(err){
      const msg = (err.data && err.data.error) || err.message || '';
      // Duplicate email: backend answers 409, or the message names an existing account.
      const duplicate = err.status === 409 || /exist|already|registered|duplicate|taken|in use/i.test(msg);
      error.value = duplicate
        ? 'An account with this email already exists — please sign in instead.'
        : (msg || 'Sign-up failed');
    }
    loading.value = false;
  }

  const kindBtn = (val, label, sub) => html`
    <button type="button" class=${'signup-kind' + (kind.value === val ? ' active' : '')}
            @mousedown=${e => e.preventDefault()} @click=${() => { kind.value = val; }}>
      <span class="signup-kind-label">${label}</span><span class="signup-kind-sub">${sub}</span>
    </button>`;

  const form = () => html`
    <form class="login-card card" @submit=${submit}>
      <div class="card-body">
        <div class="brand-lockup">
          <span class="brand-dot"></span><span class="brand-name">aboo</span>
        </div>
        <h1 class="login-title">Create your account</h1>
        <p class="login-hint mb-4">Start with aboo — set up your first company after sign-up.</p>

        <label class="form-label">I'm signing up as</label>
        <div class="signup-kinds mb-3">
          ${() => kindBtn('business', 'A business', 'I run my own books')}
          ${() => kindBtn('accountant', 'An accountant', 'I manage clients')}
        </div>

        <label class="form-label">Full name</label>
        <input class="form-control mb-3" type="text" autocomplete="name" placeholder="e.g. Joe Soap" autofocus
               @input=${e => { name.value = e.target.value; }}>
        <label class="form-label">Email</label>
        <input class="form-control mb-3" type="email" autocomplete="email" placeholder="you@company.co.za"
               @input=${e => { email.value = e.target.value; }}>
        <label class="form-label">Password</label>
        <input class="form-control mb-3" type="password" autocomplete="new-password" placeholder="At least 8 characters"
               @input=${e => { password.value = e.target.value; }}>

        ${() => error.value ? html`<div class="alert alert-danger py-2 mb-3">${error}</div>` : null}

        <button class="btn btn-primary btn-block" type="submit"
                ?disabled=${() => loading.value || !name.value || !email.value || password.value.length < 8}>
          ${() => loading.value ? 'Creating account…' : 'Create account'}
        </button>
        <p class="login-alt mt-3">Already have an account?
          <a @click=${() => navigate('/login')}>Sign in</a></p>
      </div>
    </form>`;

  const success = () => html`
    <div class="login-card card">
      <div class="card-body signup-done">
        <div class="brand-lockup"><span class="brand-dot"></span><span class="brand-name">aboo</span></div>
        <div class="signup-tick">✓</div>
        <h1 class="login-title">Welcome, ${() => done.value.name.split(' ')[0]}!</h1>
        <p class="login-hint mb-2">Your ${() => done.value.kind === 'accountant' ? 'accountant' : 'business'}
          account is ready.</p>
        <p class="login-hint mb-2">Your demo company is ready too, with practice data you can
          reset whenever you want.</p>
        <p class="login-hint mb-4">Sign in with your email <b>${() => done.value.email}</b> — that's your
          username — and the password you just chose.</p>
        <button class="btn btn-primary btn-block" @click=${() => navigate('/login')}>Continue to sign in</button>
      </div>
    </div>`;

  return html`
    <div class="login-wrap">
      <button type="button" class="theme-toggle login-toggle" title="Toggle light / dark"
              @click=${() => toggleTheme()}>${() => theme.value === 'dark' ? icon('sun', {size: 18}) : icon('moon', {size: 18})}</button>
      ${() => done.value ? success() : form()}
    </div>`;
}
